import { ComponentType, useState } from 'react';

import { CommonSegmentedToggle } from '@kaiizen/kai-shiki/react';

import { SuperAdminModal } from '../SuperAdminModal';
import { SuperAdminBatchUpload } from './SuperAdminBatchUpload';
import { SuperAdminBulkExport } from './SuperAdminBulkExport';
import { SuperAdminCreateOrg } from './SuperAdminCreateOrg';
import { SuperAdminCurrentOrg } from './SuperAdminCurrentOrg';
import { SuperAdminHealth } from './SuperAdminHealth';
import { DEFAULT_SUPER_ADMIN_TOOLS_MODAL_STYLES, SuperAdminToolsModalStyles } from './superAdminToolsModalStyles';

/** Most-used first: the modal opens on Current Org. */
const SUPER_ADMIN_TOOLS_TABS = [
  { value: 'currentOrg', label: 'Current Org', Content: SuperAdminCurrentOrg },
  { value: 'health', label: 'Server Health', Content: SuperAdminHealth },
  { value: 'createOrg', label: 'Create Org', Content: SuperAdminCreateOrg },
  { value: 'batchUpload', label: 'Batch Upload', Content: SuperAdminBatchUpload },
  { value: 'bulkExport', label: 'Bulk Export', Content: SuperAdminBulkExport },
] as const satisfies readonly { value: string; label: string; Content: ComponentType }[];

export type SuperAdminToolsTab = (typeof SUPER_ADMIN_TOOLS_TABS)[number]['value'];

/**
 * The super-admin tools, one tab each: the current org, server health, create an org, upload properties, export purchase orders.
 * A tab stays mounted (hidden) once visited, so switching tabs keeps a half-filled form.
 */
export const SuperAdminToolsModal = ({
  visible,
  tab,
  onTabChange,
  onClose,
  styles = DEFAULT_SUPER_ADMIN_TOOLS_MODAL_STYLES,
}: {
  visible: boolean;
  tab: SuperAdminToolsTab;
  onTabChange: (tab: SuperAdminToolsTab) => void;
  onClose: () => void;
  styles?: SuperAdminToolsModalStyles;
}) => {
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<SuperAdminToolsTab>>(new Set());
  if (visible && !visitedTabs.has(tab)) setVisitedTabs(new Set([...visitedTabs, tab]));

  return (
    <SuperAdminModal visible={visible} title='Super Admin' onClose={onClose}>
      <div css={styles.tabBar}>
        <CommonSegmentedToggle value={tab} options={SUPER_ADMIN_TOOLS_TABS} onChange={onTabChange} />
      </div>
      {SUPER_ADMIN_TOOLS_TABS.filter(({ value }) => visitedTabs.has(value)).map(({ value, Content }) => (
        <div key={value} css={value === tab ? undefined : styles.hiddenTab}>
          <Content />
        </div>
      ))}
    </SuperAdminModal>
  );
};
